/**
 * AGCR Neon Theme - Micro-interactions and Scroll Reveals
 * Vanilla JS only, performance-focused
 */

(function() {
  'use strict';

  // Respect reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Scroll Reveal with IntersectionObserver
   */
  function initScrollReveal() {
    if (prefersReducedMotion) return;

    const revealElements = document.querySelectorAll('.agcr-reveal');
    if (!revealElements.length) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          // Optional: unobserve after reveal to improve performance
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    revealElements.forEach(el => observer.observe(el));
  }

  /**
   * Smooth anchor scrolling
   */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#' || !href) return;

        const target = document.querySelector(href);
        if (!target) return;

        e.preventDefault();

        const headerOffset = 80;
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
      });
    });
  }

  /**
   * FAQ Accordion
   */
  function initFAQ() {
    const faqItems = document.querySelectorAll('.agcr-faq__item');
    if (!faqItems.length) return;

    faqItems.forEach(item => {
      const question = item.querySelector('.agcr-faq__question');
      if (!question) return;

      question.addEventListener('click', () => {
        const isActive = item.classList.contains('active');
        
        // Close all items
        faqItems.forEach(otherItem => {
          otherItem.classList.remove('active');
          const otherQuestion = otherItem.querySelector('.agcr-faq__question');
          if (otherQuestion) {
            otherQuestion.setAttribute('aria-expanded', 'false');
          }
        });

        // Open clicked item if it wasn't active
        if (!isActive) {
          item.classList.add('active');
          question.setAttribute('aria-expanded', 'true');
        } else {
          question.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  /**
   * Models Filter and Search
   */
  function initModelsFilter() {
    const searchInput = document.querySelector('.agcr-models__search');
    const filterChips = document.querySelectorAll('.agcr-models__filter-chip');
    const modelCards = document.querySelectorAll('.agcr-models__card');

    if (!modelCards.length) return;

    function filterModels() {
      const searchTerm = searchInput ? searchInput.value.toLowerCase() : '';
      const activeFilter = document.querySelector('.agcr-models__filter-chip.active');
      const activePlatform = activeFilter ? activeFilter.dataset.platform : 'all';

      modelCards.forEach(card => {
        const cardTitle = card.querySelector('.agcr-models__card-title')?.textContent.toLowerCase() || '';
        const cardPlatform = card.dataset.platform || '';

        const matchesSearch = !searchTerm || cardTitle.includes(searchTerm);
        const matchesFilter = activePlatform === 'all' || cardPlatform === activePlatform;

        if (matchesSearch && matchesFilter) {
          card.style.display = 'block';
          // Trigger reveal animation
          card.classList.add('agcr-reveal');
          setTimeout(() => card.classList.add('is-visible'), 10);
        } else {
          card.style.display = 'none';
        }
      });
    }

    // Search input
    if (searchInput) {
      let searchTimeout;
      searchInput.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(filterModels, 300);
      });
    }

    // Filter chips
    filterChips.forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Remove active class from all chips
        filterChips.forEach(c => c.classList.remove('active'));
        
        // Add active class to clicked chip
        chip.classList.add('active');
        
        filterModels();
      });
    });
  }

  /**
   * Neon glow on hover for cards
   */
  function initNeonGlow() {
    if (prefersReducedMotion) return;

    const glowElements = document.querySelectorAll('.agcr-stats__card, .agcr-features__card, .agcr-models__card');
    
    glowElements.forEach(element => {
      element.addEventListener('mouseenter', function() {
        this.style.transition = 'all 0.3s ease';
      });
    });
  }

  /**
   * Header Search Toggle
   */
  function initHeaderSearch() {
    const searchToggle = document.querySelector('.agcr-header__search-toggle');
    const searchContainer = document.querySelector('.agcr-header__search');
    
    if (!searchToggle || !searchContainer) return;

    searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      const isActive = searchContainer.classList.contains('active');
      
      // Close all search forms
      document.querySelectorAll('.agcr-header__search').forEach(s => {
        s.classList.remove('active');
        const toggle = s.querySelector('.agcr-header__search-toggle');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });

      // Toggle this one
      if (!isActive) {
        searchContainer.classList.add('active');
        searchToggle.setAttribute('aria-expanded', 'true');
        const input = searchContainer.querySelector('.agcr-header__search-input');
        if (input) {
          setTimeout(() => input.focus(), 100);
        }
      }
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!searchContainer.contains(e.target)) {
        searchContainer.classList.remove('active');
        searchToggle.setAttribute('aria-expanded', 'false');
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchContainer.classList.contains('active')) {
        searchContainer.classList.remove('active');
        searchToggle.setAttribute('aria-expanded', 'false');
        searchToggle.focus();
      }
    });
  }

  /**
   * Initialize all interactions
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    initScrollReveal();
    initSmoothScroll();
    initFAQ();
    initModelsFilter();
    initNeonGlow();
    initHeaderSearch();
  }

  // Start initialization
  init();
})();

